import { Skeleton, Stack, Typography } from '@mui/material';
import { DATE_FORMAT_FULL_MONTH_FNS, ScheduleStructure } from '@schooly/api';
import { DROPDOWN } from '@schooly/constants/test-ids';
import { SectionBlock, SelectPopover, TypographyWithOverflowHint } from '@schooly/style';
import { format } from 'date-fns';
import { newDateTimezoneOffset } from 'libs/api/src/lib/utils/newDateTimezoneOffset';
import { FC, useMemo, useState } from 'react';

import { useTeacherDashboardPermissions } from '../../useTeacherDashboardPermissions';
import { TimetableWidget } from './TimetableWidget';
import { useTimetableWidgetData } from './useTimetableWidgetData';
import { getWeekDays } from './utils';

const TIMETABLE_SECTION_MIN_WIDTH = 250;

export const TimetableSectionBlock: FC = () => {
  const { today, weekDays } = useMemo(() => {
    const today = newDateTimezoneOffset();
    return { today, weekDays: getWeekDays(today) };
  }, []);

  const [selectedDay, setSelectedDay] = useState(today);
  const { canViewAttendance } = useTeacherDashboardPermissions();

  const {
    structures,
    selectedStructure,
    setSelectedStructureId,
    isLoading,
    dayItems,
    pointsByGroupId,
    timeRange,
    hasStructures,
  } = useTimetableWidgetData({ selectedDay, canViewAttendance });

  if (!isLoading && !hasStructures) return null;

  if (isLoading) {
    return (
      <SectionBlock
        withCard
        sx={{ minWidth: TIMETABLE_SECTION_MIN_WIDTH }}
        title={<Skeleton width="50%" height={28} />}
      >
        <Stack p={2.5} pt={2} gap={2.5}>
          <Skeleton variant="rectangular" height={50} />
          <Skeleton variant="rectangular" height={515} />
        </Stack>
      </SectionBlock>
    );
  }

  return (
    <SectionBlock
      withCard
      sx={{ minWidth: TIMETABLE_SECTION_MIN_WIDTH }}
      title={
        <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2}>
          <Typography variant="h2" sx={{ textTransform: 'capitalize' }}>
            {format(selectedDay, DATE_FORMAT_FULL_MONTH_FNS)}
          </Typography>
          {selectedStructure && (
            <StructureSelector
              structures={structures}
              selectedStructure={selectedStructure}
              onSelect={setSelectedStructureId}
            />
          )}
        </Stack>
      }
    >
      <TimetableWidget
        weekDays={weekDays}
        selectedDay={selectedDay}
        onSelectDay={setSelectedDay}
        dayItems={dayItems}
        pointsByGroupId={pointsByGroupId}
        timeRange={timeRange}
        structure={selectedStructure}
      />
    </SectionBlock>
  );
};

interface StructureSelectorProps {
  structures: ScheduleStructure[];
  selectedStructure: ScheduleStructure;
  onSelect: (id: string) => void;
}

const StructureSelector: FC<StructureSelectorProps> = ({
  structures,
  selectedStructure,
  onSelect,
}) => {
  if (structures.length === 1)
    return (
      <TypographyWithOverflowHint variant="h3">{selectedStructure.name}</TypographyWithOverflowHint>
    );

  return (
    <Typography variant="h3" overflow="hidden" whiteSpace="nowrap">
      <SelectPopover
        dataTestId={DROPDOWN.SCHEDULE}
        options={structures}
        selectedOption={selectedStructure}
        onSelect={(structure) => onSelect(structure.id)}
        popoverProps={{
          anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
          transformOrigin: { vertical: 'top', horizontal: 'right' },
          sx: {
            '& .MuiList-root .MuiMenuItem-root': {
              whiteSpace: 'normal',
              '& > .MuiStack-root': { alignItems: 'center' },
            },
          },
        }}
      />
    </Typography>
  );
};
